import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';

/**
 * OmopCriticAgentPanel Component
 * Human-in-the-loop Critic Agent interface for iterative mapping refinement
 * Shows history of critic iterations and provides feedback interface
 *
 * @param {object} props
 * @param {array} props.criticHistory - Array of critic agent iterations
 * @param {function} props.onSubmitFeedback - Callback when user submits feedback
 * @param {boolean} props.isProcessing - Whether critic agent is currently processing
 */
const OmopCriticAgentPanel = ({
  criticHistory = [],
  onSubmitFeedback,
  isProcessing = false
}) => {
  const [feedback, setFeedback] = useState('');
  const [expandedIteration, setExpandedIteration] = useState(false);

  const handleSubmit = () => {
    if (feedback.trim() && onSubmitFeedback) {
      onSubmitFeedback(feedback);
      setFeedback('');
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedIteration(isExpanded ? panel : false);
  };

  return (
    <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PsychologyIcon color="primary" />
        <Typography variant="subtitle2" fontWeight="bold">
          Vorschlag verbessern
        </Typography>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Der Vorschlag passt nicht ganz? Geben Sie Feedback, und die KI schlägt Ihnen neue passende OMOP Concepts vor.
      </Typography>

      {/* Previous Feedback History (collapsed by default) */}
      {criticHistory.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Bisherige Verbesserungen ({criticHistory.length})
          </Typography>
          {criticHistory.map((iteration, idx) => (
            <Accordion
              key={idx}
              expanded={expandedIteration === `iteration-${idx}`}
              onChange={handleAccordionChange(`iteration-${idx}`)}
              sx={{ mb: 1, border: '1px solid #e0e0e0' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Chip label={`#${iteration.iteration}`} size="small" color="primary" variant="outlined" />
                  <Typography variant="body2" noWrap sx={{ flex: 1, overflow: 'hidden' }}>
                    {iteration.feedback.substring(0, 50)}...
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {iteration.user}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {/* User Feedback */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      Ihr Feedback:
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#fafafa' }}>
                      <Typography variant="body2">
                        {iteration.feedback}
                      </Typography>
                    </Paper>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                      von {iteration.user} • {new Date(iteration.timestamp).toLocaleString('de-DE')}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Result: Before/After Comparison */}
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      Ergebnis der Anpassung:
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {/* Previous Top Match */}
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 1.5, backgroundColor: '#fafafa', opacity: 0.7 }}
                      >
                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                          Vorher:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {iteration.previousTopMatch.conceptName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {iteration.previousTopMatch.conceptId}
                        </Typography>
                        <Chip
                          label={`${Math.round(iteration.previousTopMatch.confidenceScore * 100)}%`}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Paper>

                      {/* New Top Match */}
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 1.5, backgroundColor: '#e8f5e9', border: '2px solid #4caf50' }}
                      >
                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                          Nachher:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {iteration.newTopMatch.conceptName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {iteration.newTopMatch.conceptId}
                        </Typography>
                        <Chip
                          label={`${Math.round(iteration.newTopMatch.confidenceScore * 100)}%`}
                          size="small"
                          color="success"
                          sx={{ mt: 1 }}
                        />
                      </Paper>
                    </Box>

                    {iteration.newTopMatch.reason && (
                      <Box sx={{ mt: 1 }}>
                        <Paper variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="caption" color="textSecondary">
                            <InfoIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            {iteration.newTopMatch.reason}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Feedback Input */}
      <Box>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Ihr Feedback:
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="z.B. 'Der Vorschlag ist zu allgemein, ich suche ein spezifischeres Konzept für...' oder 'Das Konzept passt nicht, weil...'"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={isProcessing}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={isProcessing ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSubmit}
          disabled={!feedback.trim() || isProcessing}
          fullWidth
        >
          {isProcessing ? 'KI erstellt neue Vorschläge...' : 'Neue Vorschläge erhalten'}
        </Button>
      </Box>

      {isProcessing && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Die KI analysiert Ihr Feedback und erstellt neue Vorschläge. Dies kann einige Sekunden dauern...
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default OmopCriticAgentPanel;
